/**
 * The two screens Phase 1 has.
 *
 * A route per screen rather than one app that switches on state, because opening a map is
 * expensive — it replays a journal — and the URL is what says which map is open. It is also what
 * makes "the map I was working on" a link a person can keep.
 */
import { createBrowserRouter, Outlet, type RouteObject } from 'react-router';
import { AccountProvider } from './account/useAccount';
import { DeleteAccountPage } from './account/DeleteAccountPage';

import { LibraryPage } from './library/LibraryPage';
import { PrivacyPage, TermsPage } from './legal/LegalPages';

export const routes: RouteObject[] = [
  { path: '/account/email-action', lazy: async () => ({ Component: (await import('./account/EmailActionPage')).EmailActionPage }) },
  {
    path: '/share-blocks',
    lazy: async () => ({ Component: (await import('./share/SharingSafety')).ShareBlocksPage }),
  },
  {
    element: (
      <AccountProvider>
        <Outlet />
      </AccountProvider>
    ),
    children: [
      {
        path: '/moderation',
        lazy: async () => ({ Component: (await import('./share/ModerationPage')).ModerationPage }),
      },
      {
        path: '/settings',
        lazy: async () => ({ Component: (await import('./account/SettingsPage')).SettingsPage }),
      },
      {
        path: '/plans',
        lazy: async () => ({ Component: (await import('./account/PlansPage')).PlansPage }),
      },
      { path: '/delete-account', element: <DeleteAccountPage /> },
      { path: '/', element: <LibraryPage /> },
      {
        path: '/map/:mapId',
        lazy: async () => ({ Component: (await import('./editor/EditorPage')).EditorPage }),
      },
    ],
  },
  // A read-only link. `/share/*` is the API it reads from; this is the page a person opens.
  {
    path: '/s/:token',
    lazy: async () => ({ Component: (await import('./share/SharedMapPage')).SharedMapPage }),
  },
  // What the account screen asks people to agree to. Plain pages, open to everyone.
  { path: '/terms', element: <TermsPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
];

export function createRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes);
}
